import { useState, useEffect } from "preact/hooks"
import { Input } from "@/components/ui/input.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.tsx"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import { FieldType } from "@/api/org_field_config.tsx"
import { DropdownOptionsManager, DropdownOption } from "./dropdown_options_manager.tsx"

export interface FieldConfigFormData {
  field_name: string
  field_type: FieldType
  display_name: string
  display_order: number
  is_active: boolean
  table_contexts: string[]
  tempOptions?: DropdownOption[]
}

interface FieldConfigFormProps {
  config: FieldConfigFormData
  onUpdate: (config: FieldConfigFormData) => void
  showActiveField?: boolean
  showDisplayOrder?: boolean
}

export function FieldConfigForm({ 
  config, 
  onUpdate, 
  showActiveField = true,
  showDisplayOrder = true 
}: FieldConfigFormProps) {
  const [tempOptions, setTempOptions] = useState<DropdownOption[]>(config.tempOptions || [])

  useEffect(() => {
    setTempOptions(config.tempOptions || [])
  }, [config.tempOptions])

  function handleUpdate(updates: Partial<FieldConfigFormData>) {
    onUpdate({ ...config, ...updates })
  }

  function handleOptionsChange(options: DropdownOption[]) {
    setTempOptions(options)
    onUpdate({ ...config, tempOptions: options })
  }

  const fieldNameOptions = [
    { value: "product_category", label: "Product Category" },
    // The brand's accounting product group, sent on NetSuite credit memo lines (1st Phorm).
    { value: "related_product_group", label: "Related Product Group" },
    // The brand's chargeback type, sent on NetSuite credit memo lines (1st Phorm).
    { value: "chargeback_type", label: "Chargeback Type" },
    // { value: "execution_date", label: "Execution Date" },
    { value: "product_line", label: "Product Line" },
    { value: "retailer_name", label: "Retailer Name" },
    { value: "retailer_invoice_number", label: "Retailer Invoice Number" },
    // The brand's own accounting number for the deduction — Sage's ARCR for Blue Planner brands.
    { value: "remote_accounting_id", label: "Accounting ID" },
    { value: "promotion_remote_ids", label: "Remote Promo IDs" },
    { value: "admin_fee", label: "Admin fee" },
    { value: "posting_date", label: "Posting Date" },
    { value: "pushed_at", label: "Pushed Date" },
    // PromoMash's Clearing List: the posting month.
    { value: "clearing_list", label: "Clearing List" },
    // The month-end close a row was cleared on (validation date against recorded closes).
    { value: "month_end_close_list", label: "Month-End Close List" },
    { value: "validated_date", label: "Validation Date" },
    { value: "promotion_start_date", label: "Promotion Start Date" },
    { value: "promotion_end_date", label: "Promotion End Date" },
    { value: "is_trade", label: "Is Trade" },
  ]

  return (
    <div className="space-y-4">
      {/* Field Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="field_name">Field Name</Label>
          <Select
            value={config.field_name}
            onValueChange={(value) => handleUpdate({ field_name: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select field name" />
            </SelectTrigger>
            <SelectContent>
              {fieldNameOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            value={config.display_name}
            onChange={(e: Event) => {
              const target = e.target as HTMLInputElement
              handleUpdate({ display_name: target.value })
            }}
            placeholder="e.g., Product Group"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="field_type">Field Type</Label>
          <Select
            value={config.field_type}
            onValueChange={(value) => handleUpdate({ field_type: value as FieldType })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dropdown">Dropdown</SelectItem>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="date">Date</SelectItem>
              <SelectItem value="number">Number</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {showDisplayOrder && (
          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={config.display_order}
              onChange={(e: Event) => {
                const target = e.target as HTMLInputElement
                handleUpdate({ display_order: parseInt(target.value) || 0 })
              }}
            />
          </div>
        )}
      </div>

      {/* Active Field */}
      {showActiveField && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="is_active"
            checked={config.is_active}
            onCheckedChange={(checked) => handleUpdate({ is_active: !!checked })}
          />
          <Label htmlFor="is_active" className="text-sm">
            Active
          </Label>
        </div>
      )}

      {/* Table Contexts */}
      <div className="space-y-2">
        <Label>Table Contexts</Label>
        <div className="flex flex-wrap gap-4">
          {[
            { value: "deduction_table", label: "Deduction Table" },
            { value: "accounting_table", label: "Accounting Table" },
            { value: "split_form", label: "Accounting Detail Page Splits" },
            { value: "check_detail_table", label: "Check Detail Table" },
            { value: "deduction_detail", label: "Deduction detail page" },
          ].map(context => (
            <div key={context.value} className="flex items-center space-x-2">
              <Checkbox
                id={`context-${context.value}`}
                checked={config.table_contexts.includes(context.value)}
                onCheckedChange={(checked) => {
                  const newContexts = checked
                    ? [...config.table_contexts, context.value]
                    : config.table_contexts.filter(c => c !== context.value)
                  handleUpdate({ table_contexts: newContexts })
                }}
              />
              <Label htmlFor={`context-${context.value}`} className="text-sm">
                {context.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Dropdown Options */}
      {config.field_type === "dropdown" && (
        <div className="border-t pt-4">
          <DropdownOptionsManager
            options={tempOptions}
            onOptionsChange={handleOptionsChange}
          />
        </div>
      )}
    </div>
  )
}
